import { Button, Checkbox, Empty, Space, Tag, Typography } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";
import { ArrowUpDown, Eye, Pencil } from "lucide-react";
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import {
  FULFILLMENT_LINE_CATEGORY_OPTIONS,
} from "../config";
import {
  activeFulfillmentCategory,
  feeAdjustmentSpecUnit,
  fulfillmentCategoryToFilters,
  fulfillmentResourceTypeLabel,
  fulfillmentServiceProviderName,
  isReadableSupplierBusinessName,
  normalizeFulfillmentResourceType,
  pricingUnitTypeLabel,
} from "../service";
import type { FulfillmentLineFilters, FulfillmentOrderChangeBatch, FulfillmentOrderDetail, FulfillmentOrderLine } from "../types";
import { Pagination } from "../../../shared/ui/shadcn/pagination";
import { Button as ShadcnButton } from "../../../shared/ui/shadcn/button";
import {
  Table as ShadcnTable,
  TableBody as ShadcnTableBody,
  TableCell as ShadcnTableCell,
  TableHead as ShadcnTableSectionHead,
  TableHeader as ShadcnTableHead,
  TableRow as ShadcnTableRow,
} from "../../../shared/ui/shadcn/table";
import { FulfillmentOrderLineAdjustmentsPanel } from "./FulfillmentOrderLineAdjustmentsPanel";

type Props = {
  lines: FulfillmentOrderLine[];
  visibleLines: FulfillmentOrderLine[];
  filters: FulfillmentLineFilters;
  onFiltersChange: (filters: FulfillmentLineFilters) => void;
  readOnly?: boolean;
  selectedIds?: ReadonlySet<string>;
  selectableIds?: ReadonlySet<string>;
  readonlyLineIds?: ReadonlySet<string>;
  adjustableLineIds?: ReadonlySet<string>;
  onSelectedIdsChange?: (ids: Set<string>) => void;
  onEdit?: (line: FulfillmentOrderLine) => void;
  interactionDisabled?: boolean;
  adjustmentDisabled?: boolean;
  revealLineId?: string | null;
  onQuickAdjust?: (line: FulfillmentOrderLine) => void;
  onFeeAdjust?: (line: FulfillmentOrderLine) => void;
  expandedAdjustmentLineId?: string | null;
  adjustmentDetail?: FulfillmentOrderDetail | null;
  currentVersionId?: string | null;
  adjustmentLoading?: boolean;
  adjustmentError?: unknown;
  adjustmentChanges?: FulfillmentOrderChangeBatch[];
  onToggleAdjustments?: (line: FulfillmentOrderLine) => void;
  onRetryAdjustments?: () => void;
  settlementBar?: ReactNode;
};

const textOrDash = (value?: string | null) => value?.trim() || "—";
const compactNumber = (value: string | number) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/u, "").replace(/\.$/u, "");
};
const fulfillmentSupplierText = (line: FulfillmentOrderLine) => (
  (line.fulfillmentSupplierId?.trim() || line.fulfillmentSupplierName?.trim())
  && !isReadableSupplierBusinessName(line.fulfillmentSupplierName)
    ? "待重新选择"
    : textOrDash(fulfillmentServiceProviderName(line))
);
const dateRange = (line: FulfillmentOrderLine) => {
  if (line.serviceDateFrom && line.serviceDateTo) {
    return `${line.serviceDateFrom} 至 ${line.serviceDateTo}`;
  }
  return line.serviceDateFrom ?? line.serviceDateTo ?? "—";
};
const sourceLabel = (line: FulfillmentOrderLine) => (
  line.originType === "post_finalization_add"
    ? "定稿后新增"
    : line.sourceType === "history" ? "历史复制" : "手工录入"
);
const quantitySummary = (line: FulfillmentOrderLine) => (
  `${compactNumber(line.quantity)} ${line.unit} × ${compactNumber(line.billingCount)} ${feeAdjustmentSpecUnit(line.resourceType)}`
);
const resourceTypeColor = (resourceType: string) => {
  const colors: Record<string, string> = {
    stay: "blue",
    住宿: "blue",
    meal: "orange",
    餐: "orange",
    transport: "purple",
    大交通: "purple",
    包车: "purple",
    ticket: "gold",
    考察点: "gold",
    service: "cyan",
    导游: "cyan",
  };
  return colors[normalizeFulfillmentResourceType(resourceType)] ?? "gray";
};

const FULFILLMENT_LINE_TABLE_SCROLL_X = 1438;

/** 原费用选择表使用的确认行列定义。主表与合同只读列表复用行池组件。 */
export function createFulfillmentOrderLineColumns(
  renderItem?: (line: FulfillmentOrderLine) => ReactNode,
): ColumnProps<FulfillmentOrderLine>[] {
  return [
    { title: "类型", dataIndex: "resourceType", width: 84, render: (value) => <Tag color={resourceTypeColor(String(value))}>{fulfillmentResourceTypeLabel(String(value))}</Tag> },
    { title: "服务供应商", dataIndex: "fulfillmentSupplierName", width: 180, render: (_, line) => <span className="fulfillment-order-line-table__provider">{fulfillmentSupplierText(line)}</span> },
    { title: "服务项目", width: 200, render: (_, line) => <div className="fulfillment-order-resource-cell"><div className="fulfillment-order-resource-cell-heading"><span className="fulfillment-order-line-number">#{line.lineNo}</span>{renderItem ? renderItem(line) : <span className="fulfillment-order-line-link">{line.itemNameSnapshot}</span>}</div><Typography.Text type="secondary">{textOrDash(line.itemSpecSnapshot)}</Typography.Text></div> },
    { title: "服务日期", width: 125, render: (_, line) => <span className="fulfillment-order-line-table__date fulfillment-order-line-table__date--range"><span>{line.serviceDateFrom ?? "—"}</span><span>{line.serviceDateTo ?? line.serviceDateFrom ?? "—"}</span></span> },
    { title: "数量单价", width: 150, render: (_, line) => <Space direction="vertical" size={0}><span>{quantitySummary(line)}</span><Typography.Text type="secondary">{pricingUnitTypeLabel(line.pricingUnitType)} · ¥ {compactNumber(line.unitPriceAmount)}</Typography.Text></Space> },
    { title: "金额", dataIndex: "amount", width: 100, align: "right", render: (value) => <span className="money-value">¥ {compactNumber(String(value))}</span> },
    { title: "结算主体", dataIndex: "settlementSupplierName", width: 165, render: (value) => value ? <Tag color="green">{String(value)}</Tag> : <Tag color="gray">未归口</Tag> },
    { title: "备注", dataIndex: "remark", width: 170, render: (value) => textOrDash(String(value ?? "")) },
    { title: "来源", width: 100, render: (_, line) => sourceLabel(line) },
  ];
}

/** 确认件主表与费用调整原费用列表共用九个业务列，数据格式只维护一处。 */
/** 确认行池，支持未归口行全选、半选和已归口行只读展示。 */
export function FulfillmentOrderLinePool({
  lines,
  visibleLines,
  filters,
  onFiltersChange,
  readOnly = false,
  selectedIds = new Set<string>(),
  selectableIds = new Set<string>(),
  readonlyLineIds,
  adjustableLineIds,
  onSelectedIdsChange = () => undefined,
  onEdit = () => undefined,
  interactionDisabled = false,
  adjustmentDisabled = false,
  revealLineId,
  onQuickAdjust,
  onFeeAdjust,
  expandedAdjustmentLineId,
  adjustmentDetail,
  currentVersionId,
  adjustmentLoading = false,
  adjustmentError = null,
  adjustmentChanges,
  onToggleAdjustments,
  onRetryAdjustments = () => undefined,
  settlementBar,
}: Props) {
  const activeCategory = activeFulfillmentCategory(filters);
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const completedRevealRef = useRef<string | null>(null);
  const syncingScrollRef = useRef(false);
  const findTableScroller = useCallback(() => (
    tableWrapperRef.current?.querySelector<HTMLElement>(".fm-shadcn-table-wrap")
    ?? null
  ), []);
  const syncScrollLeft = useCallback((source: HTMLElement, target: HTMLElement) => {
    if (syncingScrollRef.current || target.scrollLeft === source.scrollLeft) return;
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    syncingScrollRef.current = false;
  }, []);
  const handleTopScroll = useCallback(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (topScrollbar && tableScroller) syncScrollLeft(topScrollbar, tableScroller);
  }, [findTableScroller, syncScrollLeft]);
  useEffect(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (!topScrollbar || !tableScroller) return undefined;
    const handleTableScroll = () => syncScrollLeft(tableScroller, topScrollbar);
    tableScroller.addEventListener("scroll", handleTableScroll);
    topScrollbar.scrollLeft = tableScroller.scrollLeft;
    return () => tableScroller.removeEventListener("scroll", handleTableScroll);
  }, [findTableScroller, visibleLines.length, syncScrollLeft]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pagedLines = useMemo(
    () => visibleLines.slice((page - 1) * pageSize, page * pageSize),
    [page, pageSize, visibleLines],
  );
  useEffect(() => setPage(1), [activeCategory]);
  useEffect(() => {
    if (!revealLineId) { completedRevealRef.current = null; return; }
    if (completedRevealRef.current === revealLineId) return;
    const index = visibleLines.findIndex((line) => line.id === revealLineId);
    if (index >= 0) {
      setPage(Math.floor(index / pageSize) + 1);
    }
  }, [revealLineId, visibleLines, pageSize]);
  useEffect(() => {
    if (!revealLineId || completedRevealRef.current === revealLineId || !pagedLines.some((line) => line.id === revealLineId)) return;
    const mobile = window.matchMedia?.("(max-width: 768px)").matches;
    const root = mobile ? cardsRef.current : tableWrapperRef.current;
    const target = [...(root?.querySelectorAll<HTMLElement>("[data-confirmation-line-id]") ?? [])]
      .find((element) => element.dataset.confirmationLineId === revealLineId);
    target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    target?.focus({ preventScroll: true });
    if (target) completedRevealRef.current = revealLineId;
  }, [revealLineId, pagedLines]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(visibleLines.length / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [page, pageSize, visibleLines.length]);
  const activateCategory = (index: number, shouldFocus = false) => {
    const option = FULFILLMENT_LINE_CATEGORY_OPTIONS[index];
    if (!option) return;
    if (shouldFocus) tabRefs.current[index]?.focus();
    onFiltersChange(fulfillmentCategoryToFilters(
      option.value,
      filters.sourceDifferenceOnly,
    ));
  };
  const handleCategoryKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const optionCount = FULFILLMENT_LINE_CATEGORY_OPTIONS.length;
    let targetIndex: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        targetIndex = (index + 1) % optionCount;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        targetIndex = (index - 1 + optionCount) % optionCount;
        break;
      case "Home":
        targetIndex = 0;
        break;
      case "End":
        targetIndex = optionCount - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    activateCategory(targetIndex, true);
  };
  const canSelectLine = (line: FulfillmentOrderLine) => (
    !interactionDisabled && selectableIds.has(line.id)
    && !line.fulfillmentOrderId
  );
  const actionLabel = (line: FulfillmentOrderLine) => (
    readonlyLineIds?.has(line.id)
      ? "查看"
      : adjustableLineIds?.has(line.id) ? "查看" : "编辑"
  );
  const canQuickAdjustLine = (line: FulfillmentOrderLine) => (
    !readonlyLineIds?.has(line.id)
    && Boolean(adjustableLineIds?.has(line.id) && onQuickAdjust)
  );
  const canFeeAdjustLine = (line: FulfillmentOrderLine) => (
    Boolean(onFeeAdjust && line.settlementSupplierId)
  );
  const visibleSelectableIds = visibleLines
    .filter(canSelectLine)
    .map((line) => line.id);
  const selectedCount = visibleSelectableIds.filter((id) => selectedIds.has(id)).length;
  const selection = {
    checked: visibleSelectableIds.length > 0 && selectedCount === visibleSelectableIds.length,
    indeterminate: selectedCount > 0 && selectedCount < visibleSelectableIds.length,
    selectableIds: visibleSelectableIds,
  };
  const toggleAll = (checked: boolean) => {
    const next = new Set(selectedIds);
    visibleLines.forEach((line) => {
      if (!canSelectLine(line)) next.delete(line.id);
    });
    selection.selectableIds.forEach((id) => {
      if (checked) next.add(id);
      else next.delete(id);
    });
    onSelectedIdsChange(next);
  };
  const toggleLine = (lineId: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(lineId);
    else next.delete(lineId);
    onSelectedIdsChange(next);
  };

  return (
    <section className={`fulfillment-order-line-pool${readOnly ? " fulfillment-order-line-pool--readonly" : ""}`} aria-label="确认行池">
      <div className="fulfillment-order-line-pool-toolbar">
        <div className="fulfillment-order-line-category-tabs" role="tablist" aria-label="确认行分类">
          {FULFILLMENT_LINE_CATEGORY_OPTIONS.map((option, index) => (
            <button
              type="button"
              role="tab"
              id={`${tabsId}-tab-${option.value}`}
              className="fulfillment-order-line-category-tab"
              aria-selected={activeCategory === option.value}
              aria-controls={panelId}
              tabIndex={activeCategory === option.value ? 0 : -1}
              key={option.value}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              onClick={() => activateCategory(index)}
              onKeyDown={(event) => handleCategoryKeyDown(event, index)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <span className="fulfillment-order-muted">共 {lines.length} 行，当前显示 {visibleLines.length} 行</span>
      </div>
      {!readOnly ? <div className="fulfillment-order-selection-toolbar"><Checkbox disabled={interactionDisabled} checked={selection.checked} indeterminate={selection.indeterminate} onChange={toggleAll} aria-label={`选择当前分类全部 ${visibleSelectableIds.length} 行`}>全选当前分类</Checkbox><Typography.Text type="secondary">全选范围：当前分类全部 {visibleSelectableIds.length} 行（包含其他分页）</Typography.Text></div> : null}
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={`${tabsId}-tab-${activeCategory}`}
      >
        {settlementBar}
        {visibleLines.length ? (
        <>
          <div className="fulfillment-order-line-table" role="region" aria-label="确认行表格">
            <div
              ref={topScrollbarRef}
              className="fulfillment-order-top-scrollbar"
              data-testid="fulfillment-order-top-scrollbar"
              aria-label="确认件列表顶部横向滚动条"
              onScroll={handleTopScroll}
            >
              <div className="fulfillment-order-top-scrollbar-inner" style={{ width: readOnly ? 1274 : FULFILLMENT_LINE_TABLE_SCROLL_X }} />
            </div>
            <div ref={tableWrapperRef} className="fulfillment-order-table-scroll">
              <ShadcnTable containerClassName="fulfillment-order-shadcn-table-wrap" density="compact">
                <colgroup>
                  {!readOnly && <col className="fulfillment-order-col-select" />}<col className="fulfillment-order-col-type" /><col className="fulfillment-order-col-provider" /><col className="fulfillment-order-col-item" /><col className="fulfillment-order-col-date" /><col className="fulfillment-order-col-quantity" /><col className="fulfillment-order-col-amount" /><col className="fulfillment-order-col-settlement" /><col className="fulfillment-order-col-remark" /><col className="fulfillment-order-col-source" />{!readOnly && <col className="fulfillment-order-col-actions" />}
                </colgroup>
                <ShadcnTableSectionHead>
                  <ShadcnTableRow>
                    {!readOnly && <ShadcnTableHead>选择</ShadcnTableHead>}
                    <ShadcnTableHead>类型</ShadcnTableHead><ShadcnTableHead>服务供应商</ShadcnTableHead><ShadcnTableHead>服务项目</ShadcnTableHead><ShadcnTableHead>服务日期</ShadcnTableHead><ShadcnTableHead>数量单价</ShadcnTableHead><ShadcnTableHead>金额</ShadcnTableHead><ShadcnTableHead>结算主体</ShadcnTableHead><ShadcnTableHead>备注</ShadcnTableHead><ShadcnTableHead>来源</ShadcnTableHead>{!readOnly && <ShadcnTableHead className="fulfillment-order-sticky-action">操作</ShadcnTableHead>}
                  </ShadcnTableRow>
                </ShadcnTableSectionHead>
                <ShadcnTableBody>
                  {pagedLines.map((line) => {
                    const from = line.serviceDateFrom ?? "—";
                    const to = line.serviceDateTo ?? from;
                    return <Fragment key={line.id}>
                      <ShadcnTableRow key={line.id} data-confirmation-line-id={line.id} tabIndex={revealLineId === line.id ? -1 : undefined} className={revealLineId === line.id ? "fulfillment-order-revealed-line" : undefined}>
                        {!readOnly && <ShadcnTableCell><Checkbox checked={selectedIds.has(line.id)} disabled={!canSelectLine(line)} onChange={(checked) => toggleLine(line.id, checked)} aria-label={`选择 ${line.itemNameSnapshot}`} /></ShadcnTableCell>}
                        <ShadcnTableCell><Tag className="fulfillment-order-line-type-tag" color={resourceTypeColor(line.resourceType)}>{fulfillmentResourceTypeLabel(line.resourceType)}</Tag></ShadcnTableCell>
                        <ShadcnTableCell><span className="fulfillment-order-line-table__provider" title={fulfillmentSupplierText(line)}>{fulfillmentSupplierText(line)}</span></ShadcnTableCell>
                        <ShadcnTableCell><div className="fulfillment-order-resource-cell"><div className="fulfillment-order-resource-cell-heading"><span className="fulfillment-order-line-number">#{line.lineNo}</span>{readOnly ? <span title={line.itemNameSnapshot}>{line.itemNameSnapshot}</span> : <button type="button" className="fulfillment-order-line-link" aria-expanded={expandedAdjustmentLineId === line.id} title={`${line.itemNameSnapshot}：查看增减项`} onClick={() => (onToggleAdjustments ?? onEdit)(line)}>{line.itemNameSnapshot}</button>}</div><Typography.Text type="secondary">{textOrDash(line.itemSpecSnapshot)}</Typography.Text></div></ShadcnTableCell>
                        <ShadcnTableCell><span className="fulfillment-order-line-table__date fulfillment-order-line-table__date--range" title={`${from} 至 ${to}`}><span>{from}</span><span>{to}</span></span></ShadcnTableCell>
                        <ShadcnTableCell><Space className="fulfillment-order-line-table__quantity" direction="vertical" size={0}><span>{quantitySummary(line)}</span><Typography.Text type="secondary">{pricingUnitTypeLabel(line.pricingUnitType)} · ¥ {compactNumber(line.unitPriceAmount)}</Typography.Text></Space></ShadcnTableCell>
                        <ShadcnTableCell><span className="money-value fulfillment-order-line-table__amount">¥ {compactNumber(line.amount)}</span></ShadcnTableCell>
                        <ShadcnTableCell>{line.settlementSupplierName ? <Tag className="fulfillment-order-line-table__settlement-tag" color="green" title={line.settlementSupplierName}>{line.settlementSupplierName}</Tag> : <Tag color="gray">未归口</Tag>}</ShadcnTableCell>
                        <ShadcnTableCell><span className="fulfillment-order-line-table__remark" title={textOrDash(line.remark)}>{textOrDash(line.remark)}</span></ShadcnTableCell>
                        <ShadcnTableCell><span className="fulfillment-order-line-table__source">{sourceLabel(line)}</span></ShadcnTableCell>
                        {!readOnly && <ShadcnTableCell className="fulfillment-order-sticky-action">
                          <div className="fulfillment-order-row-actions">
                            {canQuickAdjustLine(line) && onQuickAdjust ? (
                              <ShadcnButton disabled={interactionDisabled || adjustmentDisabled} variant="ghost" size="sm" aria-label={`增减 ${line.itemNameSnapshot}`} onClick={() => onQuickAdjust(line)}>
                                <ArrowUpDown aria-hidden="true" data-icon="inline-start" />增减
                              </ShadcnButton>
                            ) : null}
                            {canFeeAdjustLine(line) && onFeeAdjust ? (
                              <ShadcnButton disabled={interactionDisabled} variant="outline" size="sm" aria-label={`费用调整 ${line.itemNameSnapshot}`} onClick={() => onFeeAdjust(line)}>
                                <ArrowUpDown aria-hidden="true" data-icon="inline-start" />费用调整
                              </ShadcnButton>
                            ) : null}
                            <ShadcnButton disabled={interactionDisabled} variant="ghost" size="sm" aria-label={`${actionLabel(line)} ${line.itemNameSnapshot}`} onClick={() => onEdit(line)}>
                              {actionLabel(line) === "查看" ? <Eye aria-hidden="true" data-icon="inline-start" /> : <Pencil aria-hidden="true" data-icon="inline-start" />}
                              {actionLabel(line)}
                            </ShadcnButton>
                          </div>
                        </ShadcnTableCell>}
                      </ShadcnTableRow>
                      {expandedAdjustmentLineId === line.id ? <ShadcnTableRow key={`${line.id}-expanded`}><ShadcnTableCell colSpan={11}><FulfillmentOrderLineAdjustmentsPanel line={line} detail={adjustmentDetail ?? null} currentVersionId={currentVersionId} changes={adjustmentChanges} loading={adjustmentLoading} error={adjustmentError} onRetry={onRetryAdjustments} /></ShadcnTableCell></ShadcnTableRow> : null}
                    </Fragment>;
                  })}
                </ShadcnTableBody>
              </ShadcnTable>
            </div>
          </div>
          <div className="fulfillment-order-pagination-wrap"><Pagination page={page} pageSize={pageSize} total={visibleLines.length} pageSizeOptions={[10, 20, 50]} onChange={(nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); }} /></div>
          <div ref={cardsRef} className="fulfillment-order-line-cards">
            {pagedLines.map((line) => (
              <article className={`fulfillment-order-line-card${revealLineId === line.id ? " fulfillment-order-revealed-line" : ""}`} key={line.id} data-confirmation-line-id={line.id} tabIndex={revealLineId === line.id ? -1 : undefined}>
                <div className="fulfillment-order-card-heading">
                  {!readOnly && <Checkbox checked={selectedIds.has(line.id)} disabled={!canSelectLine(line)} onChange={(checked) => toggleLine(line.id, checked)} aria-label={`选择 ${line.itemNameSnapshot}`} />}
                  <span className="fulfillment-order-line-number">#{line.lineNo}</span>
                  {readOnly ? <strong>{line.itemNameSnapshot}</strong> : <button type="button" className="fulfillment-order-line-link" aria-expanded={expandedAdjustmentLineId === line.id} title="查看增减项" onClick={() => (onToggleAdjustments ?? onEdit)(line)}>{line.itemNameSnapshot}</button>}
                  <Tag color={resourceTypeColor(line.resourceType)}>{fulfillmentResourceTypeLabel(line.resourceType)}</Tag>
                  <Tag color={line.fulfillmentOrderId ? "green" : "orange"}>{line.fulfillmentOrderId ? "已归口" : "未归口"}</Tag>
                </div>
                <div className="fulfillment-order-line-card-grid">
                  <span>规格：{textOrDash(line.itemSpecSnapshot)}</span>
                  <span>服务供应商：{fulfillmentSupplierText(line)}</span>
                  <span>服务日期：{dateRange(line)}</span>
                  <span>数量：{quantitySummary(line)}</span>
                  <span>计价：{pricingUnitTypeLabel(line.pricingUnitType)} · ¥ {line.unitPriceAmount}</span>
                  <span>结算主体：{textOrDash(line.settlementSupplierName)}</span>
                  <span>来源：{sourceLabel(line)}</span>
                  <span>备注：{textOrDash(line.remark)}</span>
                  <strong className="money-value">金额：¥ {line.amount}</strong>
                </div>
                {!readOnly && <div className="fulfillment-order-line-card-actions">
                  {canQuickAdjustLine(line) && onQuickAdjust ? (
                    <Button disabled={interactionDisabled || adjustmentDisabled} type="text" size="small" aria-label={`增减 ${line.itemNameSnapshot}`} onClick={() => onQuickAdjust(line)}>
                      增减
                    </Button>
                  ) : null}
                  {canFeeAdjustLine(line) && onFeeAdjust ? (
                    <Button disabled={interactionDisabled} type="text" size="small" aria-label={`费用调整 ${line.itemNameSnapshot}`} onClick={() => onFeeAdjust(line)}>
                      费用调整
                    </Button>
                  ) : null}
                  <Button disabled={interactionDisabled} type="text" size="small" aria-label={`${actionLabel(line)} ${line.itemNameSnapshot}`} onClick={() => onEdit(line)}>
                    {actionLabel(line)}
                  </Button>
                </div>}
                {expandedAdjustmentLineId === line.id ? <FulfillmentOrderLineAdjustmentsPanel line={line} detail={adjustmentDetail ?? null} currentVersionId={currentVersionId} changes={adjustmentChanges} loading={adjustmentLoading} error={adjustmentError} onRetry={onRetryAdjustments} /> : null}
              </article>
            ))}
          </div>
        </>
        ) : <Empty description="暂无匹配确认行" />}
      </div>
    </section>
  );
}
